import type { AxiosRequestConfig } from 'axios';

import { conns } from '@cfg/axiosConfig.tsx';
import type { TemplateDefinitionOverrides } from '../resolveCatalogueTemplate';

export interface CatalogueTemplateSnapshot {
  design: string | null;
  overrides: TemplateDefinitionOverrides | null;
  active_override: TemplateDefinitionOverrides | null;
  version: number;
  updated_at: string | null;
  etag: string | null;
}

export interface SaveCatalogueTemplatePayload {
  design: string;
  template?: string | null;
  overrides: TemplateDefinitionOverrides | null;
  clearOtherOverrides?: boolean;
}

export interface SaveCatalogueTemplateOptions {
  etag?: string | null;
  axiosConfig?: AxiosRequestConfig;
}

const PUBLIC_RESOURCE = 'eshop:catalogue:template';
const DASHBOARD_RESOURCE = PUBLIC_RESOURCE;

type SnapshotResponse = Omit<CatalogueTemplateSnapshot, 'updated_at' | 'etag'> & {
  updated_at: string;
  etag: string | null;
};

type ApiResponse = {
  data: SnapshotResponse;
  headers: Record<string, string>;
};

const extractHeader = (headers: Record<string, string>, key: string) => {
  const normalizedKey = key.toLowerCase();
  for (const [headerKey, value] of Object.entries(headers)) {
    if (headerKey.toLowerCase() === normalizedKey) {
      return value;
    }
  }
  return undefined;
};

const normalizeSnapshot = (response: ApiResponse): CatalogueTemplateSnapshot => {
  const { data } = response;
  const etag = extractHeader(response.headers, 'ETag') ?? data.etag ?? null;
  const lastModified = extractHeader(response.headers, 'Last-Modified');
  return {
    ...data,
    etag,
    updated_at: lastModified ?? data.updated_at ?? null,
  };
};

export const fetchCatalogueTemplateSnapshot = async (): Promise<CatalogueTemplateSnapshot> => {
  const response = await conns<SnapshotResponse>(PUBLIC_RESOURCE).get();
  return normalizeSnapshot({ data: response.data, headers: (response.headers ?? {}) as Record<string, string> });
};

export const saveCatalogueTemplate = async (
  payload: SaveCatalogueTemplatePayload,
  options: SaveCatalogueTemplateOptions = {},
): Promise<CatalogueTemplateSnapshot> => {
  const { etag, axiosConfig } = options;
  const config: AxiosRequestConfig = {
    ...(axiosConfig ?? {}),
    headers: {
      ...(axiosConfig?.headers ?? {}),
      ...(etag ? { 'If-Match': etag } : {}),
    },
  };

  const response = await conns<SnapshotResponse>(DASHBOARD_RESOURCE).put(payload, config);
  return normalizeSnapshot({ data: response.data, headers: (response.headers ?? {}) as Record<string, string> });
};
